import "./styles.css";
import {
  Box,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  CardMedia,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  IconButton,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import CategoryCard from "../../components/CategoryCard/CategoryCard";
import ConfirmationModal from "../../components/ConfirmationModal/confirmationModal";
import EditLiveModal from "../../components/EditLiveModal/EditLiveModal";
import Lives from "../Lives/Lives";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import ArchiveIcon from "@mui/icons-material/Archive";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import NotAllowed from "../NotAllowed/NotAllowed";
import IconPicker, { ICON_MAP } from "../../components/IconPicker/IconPicker";
import BackgroundPicker, { BACKGROUND_MAP } from "../../components/BackgroundPicker/BackgroundPicker";
import formatDate from "../../common/Format/FormatDate";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useCategories } from "./useCategories";
import { sx, PRIMARY, COLOR_PRESETS } from "./styles";

// ── Sortable wrapper ──
const SortableCategoryCard = ({ category, lives, onCategoryClick, onEdit, canWrite }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  const color = category.color || PRIMARY;
  const IconComp = ICON_MAP[category.icon] || ICON_MAP.Category;
  const portada = BACKGROUND_MAP[category.background] || BACKGROUND_MAP.entrenamiento;

  const categoryLives = lives
    ?.filter((live) => live.categoryId === category.id)
    ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div ref={setNodeRef} style={style}>
      <CategoryCard
        name={category.name}
        color={color}
        portada={portada}
        lives={categoryLives}
        Icon={IconComp}
        order={category.order}
        isActive={category.isActive}
        onClick={() => onCategoryClick({ ...category, color, portada, lives: categoryLives })}
        onEdit={canWrite ? () => onEdit(category) : undefined}
        dragHandleProps={canWrite ? { ...attributes, ...listeners } : undefined}
      />
    </div>
  );
};

// ── Página principal ──
const Categories = () => {
  const {
    user,
    canWrite,
    lives,
    uploadLoading,
    uploadInfo,
    categories,
    loading: categoriesLoading,
    dialog,
    formName,
    formSlug,
    formColor,
    formIcon,
    formBackground,
    formIsActive,
    setFormName,
    setFormSlug,
    setFormColor,
    setFormIcon,
    setFormBackground,
    setFormIsActive,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleSave,
    handleDragEnd,
    sensors,
    loader,
    newLiveModal,
    setNewLiveModal,
    selectedCategory,
    handleCategoryClick,
    handleCloseModalCategory,
    isEditing,
    liveToEdit,
    startEditLive,
    cancelEditLive,
    onLiveSaved,
    showConfirmationModal,
    setShowConfirmationModal,
    liveToDelete,
    deleteLive,
    showArchiveConfirmationModal,
    setShowArchiveConfirmationModal,
    liveToArchive,
    openArchiveConfirmation,
    archiveLive,
  } = useCategories();

  const PreviewIcon = ICON_MAP[formIcon] || ICON_MAP.Category;

  return (
    <>
      {/* Upload banner */}
      {uploadLoading && (
        <Box sx={sx.uploadBanner}>
          <CircularProgress color="primary" size={50} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
            {uploadInfo?.title || "Subiendo..."}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary", mb: 1 }}>
            Categoría: {uploadInfo?.category || "Cargando..."}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 400, color: "text.secondary" }}>
            Subida en progreso... No cierres la página.
          </Typography>
        </Box>
      )}

      <NotAllowed user={user} page={"LIVES"} />

      {user?.admin_users?.LIVES?.READ?.[0] && (
        <>
          {/* Header */}
          <Box sx={sx.header}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={`${categories.length} categoría${categories.length !== 1 ? "s" : ""}`}
                sx={sx.categoryChip}
              />
              <Typography variant="body2" color="text.secondary">
                Arrastrá las cards para reordenar
              </Typography>
            </Stack>
            {canWrite && (
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setNewLiveModal(true)}
                  sx={sx.btnUploadLive}
                >
                  Subir Vivo
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  sx={sx.btnNewCategory}
                >
                  Nueva categoría
                </Button>
              </Stack>
            )}
          </Box>

          {/* Categories grid */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={categories.map((c) => c.id)} strategy={rectSortingStrategy}>
              <Box sx={sx.categoriesGrid}>
                {categories.map((cat) => (
                  <SortableCategoryCard
                    key={cat.id}
                    category={cat}
                    lives={lives}
                    onCategoryClick={handleCategoryClick}
                    onEdit={openEditDialog}
                    canWrite={canWrite}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        </>
      )}

      {/* Loader */}
      {(loader || categoriesLoading) && (
        <Backdrop sx={sx.loaderBackdrop} open>
          <CircularProgress color="primary" />
        </Backdrop>
      )}

      {/* Category detail modal */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={selectedCategory !== null}
      >
        <Box sx={sx.categoryDetailContainer}>
          {selectedCategory && (
            <>
              <Box sx={sx.categoryDetailHeader}>
                <Typography variant="h5" color="GrayText">
                  {selectedCategory.name}
                </Typography>
                <IconButton onClick={handleCloseModalCategory}>
                  <CloseIcon color="primary" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  backgroundImage: `url(${selectedCategory.portada})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  minHeight: 200,
                  padding: "0 3%",
                  borderTop: 3,
                  borderTopColor: selectedCategory.color,
                }}
              >
                {!selectedCategory.lives?.length ? (
                  <Typography variant="h5" sx={sx.emptyLivesText}>
                    No hay vivos para esta categoría
                  </Typography>
                ) : (
                  <List dense>
                    {selectedCategory.lives.map((item) => (
                      <ListItem
                        key={item.id}
                        sx={{ margin: "3% 0" }}
                        secondaryAction={
                          canWrite && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                              <IconButton edge="end" onClick={() => startEditLive(item)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton edge="end" onClick={() => openArchiveConfirmation(item)}>
                                <ArchiveIcon />
                              </IconButton>
                            </Box>
                          )
                        }
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <CardMedia component="img" height="auto" image={item.thumbnail} alt="Miniatura" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.title}
                          sx={{ color: "black" }}
                          secondary={
                            <>
                              <span>Creado: {formatDate(item.createdAt)}</span>
                              <br />
                              <span>Transmisión: {formatDate(item.streamingDate)}</span>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}
        </Box>
      </Backdrop>

      {/* New live modal */}
      <Backdrop open={newLiveModal} sx={{ color: "#fff", zIndex: 100 }}>
        <Lives handleClose={() => setNewLiveModal(false)} />
      </Backdrop>

      {/* Delete confirmation */}
      <ConfirmationModal
        text={`¿Eliminar el vivo "${liveToDelete?.title}"?`}
        textConfirmation="Eliminar"
        textCancel="Cancelar"
        open={showConfirmationModal}
        showConfirmationButton
        showCancelButton
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={deleteLive}
      />

      {/* Archive confirmation */}
      <ConfirmationModal
        text={`¿Archivar el vivo "${liveToArchive?.title}"?`}
        textConfirmation="Archivar"
        textCancel="Cancelar"
        open={showArchiveConfirmationModal}
        showConfirmationButton
        showCancelButton
        onClose={() => setShowArchiveConfirmationModal(false)}
        onConfirm={archiveLive}
      />

      {/* Edit live modal */}
      {isEditing && (
        <EditLiveModal
          live={liveToEdit}
          onCancelEdit={cancelEditLive}
          isEditing={isEditing}
          setIsEditing={(val) => !val && cancelEditLive()}
          onSave={onLiveSaved}
        />
      )}

      {/* Create / Edit category dialog */}
      <Dialog
        open={dialog.open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: sx.dialogPaper(formColor) }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={sx.dialogIconPreview(formColor)}>
              <PreviewIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {dialog.mode === "create" ? "Nueva categoría" : "Editar categoría"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formName || "Sin nombre"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeDialog} size="small" sx={sx.dialogCloseBtn}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {/* Name */}
            <TextField
              label="Nombre"
              placeholder="Ej: Nutrición"
              size="small"
              fullWidth
              autoFocus
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              sx={sx.textFieldFocused(formColor)}
            />

            {/* Slug */}
            <TextField
              label="Slug"
              placeholder="Ej: nutricion"
              size="small"
              fullWidth
              value={formSlug}
              onChange={(e) => setFormSlug(e.target.value)}
              helperText="Identificador para URLs. Solo letras minúsculas, números y guiones."
              sx={sx.textFieldFocused(formColor)}
            />

            {/* Color picker */}
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, mb: 1, display: "block", color: "text.secondary" }}
              >
                Color
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box sx={{ ...sx.colorPickerBox, bgcolor: formColor }}>
                  <input
                    type="color"
                    value={formColor}
                    onChange={(e) => setFormColor(e.target.value)}
                    style={sx.colorInput}
                  />
                </Box>
                <TextField
                  size="small"
                  value={formColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setFormColor(val);
                  }}
                  sx={{ width: 120 }}
                  inputProps={{ style: { fontFamily: "monospace", fontSize: 14 } }}
                />
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {COLOR_PRESETS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormColor(color)}
                    sx={sx.colorSwatch(color, formColor === color)}
                  />
                ))}
              </Box>
            </Box>

            {/* Icon picker */}
            <IconPicker selected={formIcon} onSelect={setFormIcon} color={formColor} />

            {/* Background picker */}
            <BackgroundPicker selected={formBackground} onSelect={setFormBackground} color={formColor} />

            {/* Active toggle */}
            <Box sx={sx.activeToggle(formIsActive)}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formIsActive ? "Activa" : "Inactiva"}
              </Typography>
              <Switch
                size="small"
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
                sx={sx.switchActive}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={closeDialog}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={categoriesLoading}
            sx={sx.btnSave(formColor)}
          >
            {dialog.mode === "create" ? "Crear" : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Categories;
